import React, { useEffect, useState } from "react";
import {
  Layout,
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Modal,
  Form,
  Input,
  Select,
  Upload,
  Popconfirm,
  Tooltip,
} from "antd";
import {
  FaVideo,
  FaFilePdf,
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaBook,
  FaSearch,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "../api/Api";

const { Content } = Layout;
const { Title } = Typography;
const { Option } = Select;
const { Search } = Input;

const Biblioteca = () => {
  const [conteudos, setConteudos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();
  const [tipoSelecionado, setTipoSelecionado] = useState("VIDEO");
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Estados para pesquisa
  const [searchField, setSearchField] = useState("titulo");
  const [searchText, setSearchText] = useState("");

  const userStored = JSON.parse(localStorage.getItem("user"));
  const loggedUserId = userStored?.id;

  useEffect(() => {
    document.title = "BIBLIOTECA";
    fetchConteudos();
  }, []);

  const sms_success = (m) => toast.success(m);
  const sms_error = (m) => toast.error(m);

  const fetchConteudos = async () => {
    try {
      setLoading(true);
      const resp = await api.ListLink();
      setConteudos(resp || []);
    } catch {
      sms_error("Erro ao carregar conteúdos da biblioteca");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setModalMode("add");
    setEditingItem(null);
    form.resetFields();
    setTipoSelecionado("VIDEO");
    setFileList([]);
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setModalMode("edit");
    setEditingItem(record);
    setTipoSelecionado(record.tipo);
    form.setFieldsValue({
      titulo: record.titulo,
      linha: record.linha,
      tipo: record.tipo,
      link: record.link,
    });
    setFileList([]);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const resp = await api.DeleteLink({ id });
      if (resp?.success) {
        sms_success("Conteúdo eliminado com sucesso");
        fetchConteudos();
      } else {
        sms_error("Erro ao eliminar conteúdo");
      }
    } catch {
      sms_error("Erro ao eliminar conteúdo");
    }
  };

  const handleView = (link) => {
    window.open(link, "_blank");
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      if (modalMode === "add") {
        if (values.tipo === "VIDEO") {
          const payload = {
            tipo: values.tipo,
            linha: values.linha,
            titulo: values.titulo,
            link: values.link,
            id_user: loggedUserId,
          };
          const resp = await api.AddLink(payload);
          if (resp?.success) {
            sms_success("Vídeo adicionado com sucesso");
            setModalVisible(false);
            fetchConteudos();
          } else {
            sms_error("Erro ao adicionar vídeo");
          }
        } else {
          if (fileList.length === 0) {
            sms_error("Selecione um arquivo PDF");
            return;
          }

          setUploading(true);
          const formData = new FormData();
          formData.append("file", fileList[0]);
          formData.append("titulo", values.titulo || "");
          formData.append("linha", values.linha || "");
          formData.append("tipo", values.tipo);
          formData.append("id_user", loggedUserId);

          const resp = await api.AddPdf(formData);
          if (resp?.success) {
            sms_success("PDF adicionado com sucesso");
            setModalVisible(false);
            fetchConteudos();
          } else {
            sms_error("Erro ao adicionar PDF");
          }
          setUploading(false);
        }
      } else {
        const payload = {
          id: editingItem.id,
          tipo: values.tipo,
          titulo: values.titulo,
          link: values.link,
        };
        const resp = await api.AlterLink(payload);
        if (resp?.success) {
          sms_success("Vídeo atualizado com sucesso");
          setModalVisible(false);
          fetchConteudos();
        } else {
          sms_error("Erro ao atualizar vídeo");
        }
      }
    } catch (error) {
      console.error("Erro no formulário:", error);
    }
  };

  const handleTipoChange = (value) => {
    setTipoSelecionado(value);
    if (value === "PDF") {
      form.setFieldsValue({ link: undefined });
    }
  };

  const uploadProps = {
    beforeUpload: (file) => {
      const isPDF = file.type === "application/pdf";
      if (!isPDF) {
        sms_error("Apenas arquivos PDF são permitidos");
        return Upload.LIST_IGNORE;
      }
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        sms_error("O arquivo deve ter menos de 2MB");
        return Upload.LIST_IGNORE;
      }
      setFileList([file]);
      return false;
    },
    onRemove: () => setFileList([]),
    fileList,
    maxCount: 1,
  };

  // Função de filtro para pesquisa
  const filtrarConteudos = () => {
    if (!searchText.trim()) return conteudos;

    return conteudos.filter((item) => {
      const campo = item[searchField]?.toString().toLowerCase() || "";
      return campo.includes(searchText.toLowerCase());
    });
  };

  const dadosFiltrados = filtrarConteudos();

  const columns = [
    {
      title: "Título",
      dataIndex: "titulo",
      key: "titulo",
    },
    {
      title: "Linha",
      dataIndex: "linha",
      key: "linha",
      render: (linha) => <Tag color="blue">{linha || "GERAL"}</Tag>,
    },
    {
      title: "Tipo",
      dataIndex: "tipo",
      key: "tipo",
      render: (tipo) =>
        tipo === "VIDEO" ? (
          <Tag icon={<FaVideo />} color="purple">
            VÍDEO
          </Tag>
        ) : (
          <Tag icon={<FaFilePdf />} color="red">
            PDF
          </Tag>
        ),
    },
    {
      title: "Ações",
      key: "acoes",
      render: (_, record) => {
        const isOwner = record.id_user === loggedUserId;
        return (
          <Space size="small">
            <Tooltip title="Visualizar">
              <Button
                type="link"
                icon={<FaEye />}
                onClick={() => handleView(record.link)}
              />
            </Tooltip>
            {isOwner && (
              <>
                {record.tipo === "VIDEO" && (
                  <Tooltip title="Editar">
                    <Button
                      type="link"
                      icon={<FaEdit />}
                      onClick={() => handleEdit(record)}
                    />
                  </Tooltip>
                )}
                <Tooltip title="Eliminar">
                  <Popconfirm
                    title="Tem certeza que deseja eliminar este conteúdo?"
                    onConfirm={() => handleDelete(record.id)}
                    okText="Sim"
                    cancelText="Não"
                  >
                    <Button type="link" danger icon={<FaTrash />} />
                  </Popconfirm>
                </Tooltip>
              </>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <Layout>
      <Content style={{ padding: 24 }}>
        <ToastContainer />

        <Card
          title={
            <Space>
              <FaBook style={{ fontSize: 22, color: "#1677ff" }} />
              <Title level={4} style={{ margin: 0 }}>
                Biblioteca de Conteúdos
              </Title>
            </Space>
          }
          extra={
            <Button type="primary" icon={<FaPlus />} onClick={handleAdd}>
              Adicionar Conteúdo
            </Button>
          }
        >
          {/* Linha de pesquisa e estatísticas */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} md={16}>
              <Space.Compact style={{ width: "100%" }}>
                <Select
                  value={searchField}
                  onChange={setSearchField}
                  style={{ width: 120 }}
                >
                  <Option value="titulo">Título</Option>
                  <Option value="linha">Linha</Option>
                  <Option value="tipo">Tipo</Option>
                </Select>
                <Search
                  placeholder={`Pesquisar por ${searchField}...`}
                  allowClear
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onSearch={(value) => setSearchText(value)}
                  enterButton={<FaSearch />}
                  style={{ width: "100%" }}
                />
              </Space.Compact>
            </Col>
            <Col xs={24} md={8}>
              <Card size="small" hoverable bodyStyle={{ padding: "12px 16px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-around" }}>
                  <Space>
                    <FaVideo style={{ fontSize: 18, color: "#722ed1" }} />
                    <span>{conteudos.filter(c => c.tipo === "VIDEO").length}</span>
                  </Space>
                  <Space>
                    <FaFilePdf style={{ fontSize: 18, color: "#ff4d4f" }} />
                    <span>{conteudos.filter(c => c.tipo === "PDF").length}</span>
                  </Space>
                </div>
              </Card>
            </Col>
          </Row>

          <Table
            columns={columns}
            dataSource={dadosFiltrados}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10 }}
            locale={{ emptyText: "Nenhum conteúdo encontrado" }}
          />
        </Card>

        {/* Modal de Adicionar/Editar (sem alterações) */}
        <Modal
          title={modalMode === "add" ? "Adicionar Conteúdo" : "Editar Vídeo"}
          open={modalVisible}
          onOk={handleModalOk}
          onCancel={() => setModalVisible(false)}
          confirmLoading={uploading}
          okText={modalMode === "add" ? "Adicionar" : "Salvar"}
          cancelText="Cancelar"
          width={600}
        >
          <Form form={form} layout="vertical" name="bibliotecaForm">
            <Form.Item
              name="tipo"
              label="Tipo de Conteúdo"
              rules={[{ required: true, message: "Selecione o tipo" }]}
            >
              <Select onChange={handleTipoChange} disabled={modalMode === "edit"}>
                <Option value="VIDEO">Vídeo (Link)</Option>
                <Option value="PDF">Documento PDF</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="titulo"
              label="Título"
              rules={[{ required: true, message: "Informe o título" }]}
            >
              <Input   />
            </Form.Item>

            <Form.Item
              name="linha"
              label="Linha"
              rules={[{ required: true, message: "Selecione a linha" }]}
            >
              <Select placeholder="Selecione a linha">
                <Option value="LAEE">LAEE</Option>
                <Option value="IPU">IPU</Option>
                <Option value="MOVICEL">MOVICEL</Option>
                <Option value="GERAL">GERAL</Option>
              </Select>
            </Form.Item>

            {tipoSelecionado === "VIDEO" && (
              <Form.Item
                name="link"
                label="Link do Vídeo"
                rules={[
                  { required: true, message: "Informe o link do vídeo" },
                  { type: "url", message: "URL inválida" },
                ]}
              >
                <Input   />
              </Form.Item>
            )}

            {tipoSelecionado === "PDF" && modalMode === "add" && (
              <Form.Item
                label="Arquivo PDF"
                required
                tooltip="Apenas arquivos PDF até 2MB"
              >
                <Upload {...uploadProps} accept=".pdf">
                  <Button icon={<FaFilePdf />}>Selecionar PDF</Button>
                </Upload>
              </Form.Item>
            )}

            {modalMode === "edit" && (
              <div style={{ color: "#666", marginTop: 16 }}>
                <p>
                  <strong>Nota:</strong> Apenas o título e o link podem ser editados.
                </p>
              </div>
            )}
          </Form>
        </Modal>
      </Content>
    </Layout>
  );
};

export default Biblioteca;